import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  COACHING_THREE_SIX_NINE_LABELS,
  COACHING_THREE_SIX_NINE_SESSIONS,
  type CoachingThreeSixNineSession,
} from '@mlm/shared/coaching';
import {
  CoachButton,
  ErrorNote,
  Eyebrow,
  Field,
  inputClass,
  Loading,
  SageCheck,
  Screen,
  Stat,
} from '../components/primitives';
import { coaching, type ThreeSixNineEntry, type ThreeSixNineState } from '../lib/api';
import { qk, useProfile, useRefreshCoaching, useToday } from '../lib/queries';

/** The session the clock suggests, in the member's own timezone (the prototype's windows). */
function sessionNow(timeZone: string): CoachingThreeSixNineSession {
  let hour = new Date().getHours();
  try {
    hour = Number(
      new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone }).format(
        new Date(),
      ),
    );
  } catch {
    /* an unknown zone falls back to the device clock */
  }
  return hour < 11 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
}

const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

/** SC-25: one affirmation, written 3 times in the morning, 6 in the afternoon, 9 in the evening. */
export function ThreeSixNinePage() {
  const state = useQuery({ queryKey: qk.threeSixNine, queryFn: coaching.threeSixNine });
  const today = useToday();
  const profile = useProfile();
  if (state.isPending || today.isPending || profile.isPending) return <Loading />;
  if (state.isError) return <ErrorNote error={state.error} retry={() => void state.refetch()} />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  if (profile.isError)
    return <ErrorNote error={profile.error} retry={() => void profile.refetch()} />;
  return (
    <Practice
      state={state.data}
      suggestion={today.data.affirmation}
      current={sessionNow(profile.data.profile.timezone)}
    />
  );
}

function Practice({
  state,
  suggestion,
  current,
}: {
  state: ThreeSixNineState;
  suggestion: string;
  current: CoachingThreeSixNineSession;
}) {
  const entry = state.entry;
  const lockedAffirmation = COACHING_THREE_SIX_NINE_SESSIONS.some((s) => entry?.sessions[s].doneAt);
  const [affirmation, setAffirmation] = useState(entry?.affirmation ?? suggestion);
  useEffect(() => {
    if (entry) setAffirmation(entry.affirmation);
  }, [entry]);

  return (
    <Screen
      back="/manifest"
      eyebrow="369 practice"
      title="369 practice"
      subtitle="Repeat one intentional affirmation through the day as a structured focus exercise."
      aside={
        <Stat
          compact
          eyebrow="Days in a row"
          value={days(state.streak)}
          detail={entry?.complete ? "Today's 369 practice is complete." : undefined}
        />
      }
    >
      <section className="border-b border-border px-5 py-6">
        {lockedAffirmation ? (
          <>
            <Eyebrow className="mb-2">Today's affirmation</Eyebrow>
            <p className="font-display text-[1.5rem] italic leading-snug text-ink lg:text-[1.875rem]">
              {entry!.affirmation}
            </p>
          </>
        ) : (
          <Field
            label="Today's affirmation"
            htmlFor="three-six-nine-affirmation"
            hint="Write it as if it is already true: present tense, specific, and short enough to write 18 times today."
          >
            <input
              id="three-six-nine-affirmation"
              className={inputClass}
              maxLength={220}
              value={affirmation}
              onChange={(e) => setAffirmation(e.target.value)}
            />
          </Field>
        )}
      </section>
      {COACHING_THREE_SIX_NINE_SESSIONS.map((session) => (
        <SessionBlock
          key={`${session}:${entry?.sessions[session].doneAt ?? 'open'}`}
          session={session}
          count={state.counts[session]}
          entry={entry}
          affirmation={affirmation.trim()}
          highlighted={session === current}
        />
      ))}
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        Missed a session? Continue from where you are. Consistency matters more than perfection.
      </p>
    </Screen>
  );
}

function SessionBlock({
  session,
  count,
  entry,
  affirmation,
  highlighted,
}: {
  session: CoachingThreeSixNineSession;
  count: number;
  entry: ThreeSixNineEntry | null;
  affirmation: string;
  highlighted: boolean;
}) {
  const refresh = useRefreshCoaching();
  const saved = entry?.sessions[session];
  const done = Boolean(saved?.doneAt);
  const [lines, setLines] = useState<string[]>(() =>
    Array.from({ length: count }, (_, i) => saved?.lines[i] ?? ''),
  );
  const filled = useMemo(() => lines.map((l) => l.trim()).filter(Boolean), [lines]);
  const label = COACHING_THREE_SIX_NINE_LABELS[session];

  const save = useMutation({
    mutationFn: () => coaching.saveThreeSixNine({ affirmation, session, lines: filled }),
    onSuccess: (result) => {
      if (result.justCompleted) {
        coaching.event('three_six_nine_completed', { session });
      }
      void refresh();
    },
    meta: {
      successMessage: filled.length === count ? `${label} session complete.` : 'Progress saved.',
    },
  });

  return (
    <section className="border-b border-border px-5 py-6">
      <div className="flex items-baseline justify-between gap-3">
        <Eyebrow>
          {label} · {count} times
        </Eyebrow>
        {done && (
          <span className="flex items-center gap-2 text-sm text-sage">
            <SageCheck size={18} /> Done
          </span>
        )}
      </div>
      {done ? (
        <ol className="mt-3 space-y-1">
          {saved!.lines.map((line, i) => (
            <li key={i} className="text-sm leading-relaxed text-ink/60">
              {line}
            </li>
          ))}
        </ol>
      ) : (
        <>
          <div className="mt-4 space-y-2">
            {lines.map((value, i) => (
              <div key={i}>
                <label htmlFor={`three-six-nine-${session}-${i}`} className="sr-only">
                  {label} line {i + 1}
                </label>
                <input
                  id={`three-six-nine-${session}-${i}`}
                  className={inputClass}
                  maxLength={220}
                  value={value}
                  placeholder={i === 0 ? affirmation || 'Write your affirmation' : undefined}
                  onChange={(e) => setLines(lines.map((l, j) => (j === i ? e.target.value : l)))}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <CoachButton
              kind={highlighted ? 'gold' : 'outline'}
              disabled={filled.length === 0 || affirmation.length < 3 || save.isPending}
              onClick={() => save.mutate()}
            >
              {filled.length === count ? `Complete ${label.toLowerCase()}` : 'Save progress'}
            </CoachButton>
            <span className="eyebrow">
              {filled.length} of {count}
            </span>
          </div>
        </>
      )}
    </section>
  );
}
